'use client';

import { useLayoutEffect, useState, type RefObject } from 'react';

interface ChipRowFitOptions {
  /** The wrapping row whose width the chips must fit. */
  containerRef: RefObject<HTMLElement | null>;
  /** An invisible, unwrapped copy of every chip followed by the "+ N more" chip. */
  measureRef: RefObject<HTMLElement | null>;
  maxRows: number;
  /** Must match the row's CSS `gap`. */
  gap: number;
  /** Rendered until the first client measurement (SSR and hydration). */
  fallback: number;
}

/**
 * How many chips fit in `maxRows` rows next to the "+ N more" chip. Widths are
 * read from the measure row because a hidden chip measures 0.
 */
export const useChipRowFit = ({
  containerRef,
  measureRef,
  maxRows,
  gap,
  fallback,
}: ChipRowFitOptions): number => {
  const [count, setCount] = useState(fallback);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return undefined;

    const fit = () => {
      const width = container.clientWidth;
      const nodes = Array.from(measure.children) as HTMLElement[];
      if (nodes.length < 2 || width <= 0) return;
      const moreWidth = nodes[nodes.length - 1]!.offsetWidth;
      const widths = nodes.slice(0, -1).map((node) => node.offsetWidth);

      const rowsFor = (n: number, withMore: boolean): number => {
        const items = widths.slice(0, n);
        if (withMore) items.push(moreWidth);
        let rows = 1;
        let row = -gap;
        for (const w of items) {
          if (row + gap + w <= width || row < 0) {
            row += gap + w;
          } else {
            rows += 1;
            row = w;
          }
        }
        return rows;
      };

      if (rowsFor(widths.length, false) <= maxRows) {
        setCount(widths.length);
        return;
      }
      for (let n = widths.length - 1; n >= 0; n -= 1) {
        if (rowsFor(n, true) <= maxRows) {
          setCount(n);
          return;
        }
      }
      setCount(0);
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(container);
    observer.observe(measure);
    return () => observer.disconnect();
  }, [containerRef, measureRef, maxRows, gap]);

  return count;
};
